import { Box, Text } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';

import authAxios from '@services/authAxios';
import { useBundleStore } from '../state/useAddProductBundle';
import { useFileUploadPopUp } from '@store/useFileUploadPopUp';
import { AppProvider, useApp } from '@pages/UploadMappingV2/context/AppContext';
import { useSnackbarNotification } from '@store/snackbarNotification';
import PostUploadModal from '@pages/UploadMappingV2/components/postUploadModal';
import { usePieceStore } from '../state/useAddProductSatuan';
import { useMassalStore } from '../state/useAddProductMassal';
import { tranformData } from '@pages/UploadMappingV2/utils';
import getUploadTutorial from '@hooks/tutorial/upload/getUpload';

/**
 * Local store to keep 'before' data for rollback between different mutations. Using useState is not possible because how react lifecycle works with mutations.
 */
const beforeStore = create((set) => ({
    before: null,
    setBefore: (before) => set({ before }),
    resetBefore: () => set({ before: null }),
}));

const useMutateUploadMapping = () => {
    const navigate = useNavigate();
    const { before, setBefore, resetBefore } = beforeStore();
    const { pieces, resetPieces, setIsLoading } = usePieceStore();
    const { bundles, resetBundles, setIsLoading: setIsLoadingBundle } = useBundleStore();
    const { setData, setIsLoading: setIsLoadingMassal, data: dataMassal } = useMassalStore();
    const { data: uploadTutorial } = getUploadTutorial();

    const { reset } = useFileUploadPopUp();
    const queryClient = useQueryClient();
    const { set } = useFileUploadPopUp();
    const { onChangeSnackbarNotification } = useSnackbarNotification();

    const { onCloseModalConfirmAddProductBundle, onCloseModalConfirmAddProductPiece } = useApp();

    const uploadProdukMassal = useMutation({
        mutationFn: async ({ files }) => {
            setIsLoadingMassal(true);
            const formData = new FormData();
            formData.append('file', files[0]);

            const { data } = await authAxios.post('/api/v2/sellout/product-mapping/upload-massal', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (ev) => {
                    const uploadProgress = Math.round((ev.loaded * 100) / ev.total);
                    set({ uploadProgress });
                },
            });
            return data;
        },
        onSuccess: (data) => {
            const transformData = tranformData(data.data);
            setData(transformData);

            set({
                isFinishUploading: true,
                renderContent: (
                    <AppProvider>
                        <PostUploadModal />
                    </AppProvider>
                ),
            });
        },
        onError: (error) => {},
        onSettled: () => {
            setIsLoadingMassal(false);
        },
    });

    const validateUploadProdukMassal = useMutation({
        mutationFn: async ({ payload }) => {
            setIsLoadingMassal(true);
            const { data } = await authAxios.post('/api/v2/sellout/product-mapping/validate-massal', payload);
            return data;
        },
        onSuccess: (data) => {
            const localBefore = dataMassal;
            setBefore(localBefore);
            setData(data.data);
            set({
                isOpen: true,
                isFinishUploading: true,
                title: 'Upload Produk',
                isUploading: true,
                helperText: {
                    isShow: true,
                    text: 'Temukan Panduan Mapping Disini!',
                    isLink: true,
                    url: uploadTutorial,
                },
                renderContent: (
                    <AppProvider>
                        <PostUploadModal
                            onCancel={() => {
                                setTimeout(() => setData(localBefore), 200);
                                resetBefore();
                            }}
                        />
                    </AppProvider>
                ),
            });
        },
        onError: (error) => {},
        onSettled: () => {
            setIsLoadingMassal(false);
        },
    });

    const confirmUploadProdukMassal = useMutation({
        mutationFn: async ({ payload }) => {
            setIsLoadingMassal(true);
            const { data } = await authAxios.post('/api/v2/sellout/product-mapping/confirm-massal', payload);
            return data;
        },
        onSuccess: (data) => {
            const description = data?.data;

            onChangeSnackbarNotification({
                status: 'success',
                title: 'Sukses Upload',
                description,
                duration: 5,
            });

            set({ isOpen: false });

            queryClient.invalidateQueries({
                queryKey: ['product-satuan', 'product-bundle'],
            });
            navigate(-1);
        },
        onError: (error) => {
            const title = 'Gagal menyimpan mapping produk.';
            const description = error?.response?.data?.message || 'Terjadi kesalahan saat menyimpan mapping produk.';
            set({ isOpen: true });
            onChangeSnackbarNotification({
                status: 'error',
                title,
                description,
                duration: 5,
            });
        },
        onSettled: () => {
            resetBefore();
            setIsLoadingMassal(false);
        },
    });

    const uploadProductBundle = useMutation({
        mutationFn: async ({ payload }) => {
            setIsLoadingBundle(true);
            const { data } = await authAxios.post('/api/v2/sellout/product-mapping', payload);
            return data;
        },
        onSuccess: () => {
            const totalItems = bundles.reduce((sum, b) => sum + b.items.length, 0);
            onChangeSnackbarNotification({
                status: 'success',
                title: 'Sukses Mapping',
                description: (
                    <Text>
                        <Box fontWeight={'bold'} display={'inline'}>
                            {totalItems} Produk
                        </Box>{' '}
                        berhasil terhubung dengan produk Paragon
                    </Text>
                ),
                duration: 5,
            });

            onCloseModalConfirmAddProductBundle();
            resetBundles();

            queryClient.invalidateQueries({
                queryKey: ['product-bundle'],
            });
        },
        onError: (error) => {
            const title = 'Gagal menyimpan mapping produk.';
            const description = error?.response?.data?.message || 'Terjadi kesalahan saat menyimpan mapping produk.';

            onChangeSnackbarNotification({
                status: 'error',
                title,
                description,
                duration: 5,
            });
        },
        onSettled: () => {
            setIsLoadingBundle(false);
        },
    });

    const editProductBundle = useMutation({
        mutationFn: async ({ id, payload }) => {
            setIsLoadingBundle(true);
            const { data } = await authAxios.patch(`/api/v2/sellout/product-mapping/edit/${id}?type=bundle`, payload);
            return data;
        },
        onSuccess: (data) => {
            const totalItems = data?.total;
            onChangeSnackbarNotification({
                status: 'success',
                title: 'Mapping Sukses Diperbaharui',
                description: (
                    <Text>
                        <Box fontWeight={'bold'} display={'inline'}>
                            {totalItems} Produk
                        </Box>{' '}
                        berhasil terhubung dengan produk Paragon
                    </Text>
                ),
                duration: 5,
            });

            onCloseModalConfirmAddProductBundle();
            resetBundles();

            queryClient.invalidateQueries({
                queryKey: ['product-bundle'],
            });
        },
        onError: (error) => {
            const title = 'Gagal menyimpan mapping produk.';
            const description = error?.response?.data?.message || 'Terjadi kesalahan saat menyimpan mapping produk.';

            onChangeSnackbarNotification({
                status: 'error',
                title,
                description,
                duration: 5,
            });
        },
        onSettled: () => {
            setIsLoadingBundle(false);
        },
    });

    const deleteProductBundle = useMutation({
        mutationFn: async (id) => {
            setIsLoadingBundle(true);
            const { data } = await authAxios.delete(`/api/v2/sellout/product-mapping/${id}?type=bundle`);
            return data;
        },
        onSuccess: (data) => {
            const totalItems = data?.total;
            onChangeSnackbarNotification({
                status: 'warning-check',
                title: 'Mapping Telah Dihapus',
                description: (
                    <Text>
                        <Box fontWeight={'bold'} display={'inline'}>
                            {totalItems} Produk
                        </Box>{' '}
                        tidak lagi terhubung dengan produk Paragon
                    </Text>
                ),
                duration: 5,
            });

            queryClient.invalidateQueries({
                queryKey: ['product-bundle'],
            });
        },
        onError: (error) => {
            const title = 'Gagal menghapus mapping produk.';
            const description = error?.response?.data?.message || 'Terjadi kesalahan saat menghapus mapping produk.';

            onChangeSnackbarNotification({
                status: 'error',
                title,
                description,
                duration: 5,
            });
        },
        onSettled: () => {
            setIsLoadingBundle(false);
        },
    });

    const uploadProductSatuan = useMutation({
        mutationFn: async ({ payload }) => {
            setIsLoading(true);
            const { data } = await authAxios.post('/api/v2/sellout/product-mapping', payload);
            return data;
        },
        onSuccess: (data) => {
            const totalItems = pieces.length;
            onChangeSnackbarNotification({
                status: 'success',
                title: 'Sukses Mapping',
                description: (
                    <Text>
                        <Box fontWeight={'bold'} display={'inline'}>
                            {totalItems} Produk
                        </Box>{' '}
                        berhasil terhubung dengan produk Paragon
                    </Text>
                ),
                duration: 5,
            });

            onCloseModalConfirmAddProductPiece();
            resetPieces();

            queryClient.invalidateQueries({
                queryKey: ['product-satuan'],
            });
        },
        onError: (error) => {
            const title = 'Gagal menyimpan mapping produk.';
            const description = error?.response?.data?.message || 'Terjadi kesalahan saat menyimpan mapping produk.';

            onChangeSnackbarNotification({
                status: 'error',
                title,
                description,
                duration: 5,
            });
        },
        onSettled: () => {
            setIsLoading(false);
        },
    });

    const editProductSatuan = useMutation({
        mutationFn: async ({ id, payload }) => {
            setIsLoading(true);
            const { data } = await authAxios.patch(`/api/v2/sellout/product-mapping/edit/${id}?type=piece`, payload);
            return data;
        },
        onSuccess: (data) => {
            const totalItems = 1;
            onCloseModalConfirmAddProductPiece();

            queryClient.invalidateQueries({
                queryKey: ['product-satuan'],
            });

            onChangeSnackbarNotification({
                status: 'success',
                title: 'Mapping Sukses Diperbaharui',
                description: (
                    <Text>
                        <Box fontWeight={'bold'} display={'inline'}>
                            {totalItems} Produk
                        </Box>{' '}
                        berhasil terhubung dengan produk Paragon
                    </Text>
                ),
                duration: 5,
            });
        },
        onError: (error) => {
            const title = 'Gagal menyimpan mapping produk.';
            const description = error?.response?.data?.message || 'Terjadi kesalahan saat menyimpan mapping produk.';

            onChangeSnackbarNotification({
                status: 'error',
                title,
                description,
                duration: 5,
            });
        },
        onSettled: () => {
            resetPieces();
            setIsLoading(false);
        },
    });

    const deleteProductSatuan = useMutation({
        mutationFn: async (id) => {
            setIsLoading(true);
            const { data } = await authAxios.delete(`/api/v2/sellout/product-mapping/${id}?type=piece`);
            return data;
        },
        onSuccess: (data) => {
            const totalItems = 1;

            queryClient.invalidateQueries({
                queryKey: ['product-satuan'],
            });

            onChangeSnackbarNotification({
                status: 'warning-check',
                title: 'Mapping Telah Dihapus',
                description: (
                    <Text>
                        <Box fontWeight={'bold'} display={'inline'}>
                            {totalItems} Produk
                        </Box>{' '}
                        tidak lagi terhubung dengan produk Paragon
                    </Text>
                ),
                duration: 5,
            });
        },
        onError: (error) => {
            const title = 'Gagal menghapus mapping produk.';
            const description = error?.response?.data?.message || 'Terjadi kesalahan saat menghapus mapping produk.';

            onChangeSnackbarNotification({
                status: 'error',
                title,
                description,
                duration: 5,
            });
        },
        onSettled: () => {
            setIsLoading(false);
        },
    });

    return {
        uploadProdukMassal,
        validateUploadProdukMassal,
        confirmUploadProdukMassal,

        uploadProductBundle,
        editProductBundle,
        deleteProductBundle,

        uploadProductSatuan,
        editProductSatuan,
        deleteProductSatuan,
    };
};

export default useMutateUploadMapping;
